import React, { useEffect, useRef, useState } from "react";
import LogoReale from "../assets/images/Logo_RealeGroup.svg";

const NAV = [
  { label: "", target: "#hero" },
  { label: "Lettera agli Stakeholder", target: "#sezione-lettere" },
  { label: "Perimetro di consolidamento", target: "#sezione-perimetro" },
  { label: "Principali eventi dell'esercizio", target: "#sezione-eventi" },
  { label: "I nostri numeri", target: "#sezione-numeri" },
  { label: "Sezioni e highlights", target: "#sezione-sezioni" },
];

export default function Header({ color }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const headerRef = useRef(null);
  const barRef = useRef(null);

  const shouldUseBlueBg = scrolled || color === true;

  // Shadow + bg on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 2);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Smooth scroll with header offset
  const scrollToTarget = (selector) => {
    const el = document.querySelector(selector);
    if (!el) return;

    const run = () => {
      const barH = barRef.current?.offsetHeight || 0;
      const top = el.getBoundingClientRect().top + window.pageYOffset - barH - 8;
      window.scrollTo({ top, behavior: "smooth" });
    };

    const wasOpen = open;
    setOpen(false);
    if (wasOpen) {
      setTimeout(run, 320);
    } else {
      run();
    }
  };


  const onAnchorClick = (e, target) => {
    const path = window.location.pathname.replace(/\/index\.html$/, "");
    const onHome = path === "" || path === "/";
    if (onHome) {
      e.preventDefault();
      scrollToTarget(target);
    }
  };


  useEffect(() => {
    const tryScrollToHash = () => {
      if (window.location.hash) {
        // rAF doppio per essere sicuri che DOM e layout siano pronti
        requestAnimationFrame(() => requestAnimationFrame(() => {
          scrollToTarget(window.location.hash);
        }));
      }
    };

    tryScrollToHash();
    const onHashChange = () => tryScrollToHash();
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);


  useEffect(() => {
    const sections = NAV.map((item) => document.querySelector(item.target)).filter(Boolean);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1, // come versione originale (se vuoi più "reattività", usa 0.6)
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-20 transition-colors duration-300
        ${shouldUseBlueBg ? "bg-[#004996] shadow-sm" : "bg-transparent"}`}
    >
      {/* Barra alta */}
      <div
        ref={barRef}
        className="mx-auto w-full xl:w-[1440px] px-5 flex items-center gap-4 py-6"
      >
        <div className="flex w-1/3">
          <a href="https://www.realegroup.eu/IT/" target="_blank" rel="noreferrer noopener">
            <img src={LogoReale} alt="Logo Reale Group" className="w-[120px] md:w-[150px]" />
          </a>
        </div>

        <div className="flex w-2/3 flex-row justify-end">
          <nav className="hidden min-[1084px]:block">
            <ul className="flex gap-8 text-white">
              {NAV.filter((item) => item.label).map((item) => (
                <li key={item.target}>
                  {color ? (
                    // color === true → link reale a home + anchor
                    <a
                      href={`/${item.target}`} // es. "/#sezione-lettere"
                      onClick={(e) => onAnchorClick(e, item.target)}
                      className={`hover:underline focus:outline-none md:text-[18px] xl:text-[22px] transition-all duration-150 text-left
                        ${activeSection === item.target ? "font-bold" : ""}`}
                    >
                      {item.label}
                    </a>
                  ) : (
                    // altrimenti → smooth scroll interno
                    <button
                      onClick={() => scrollToTarget(item.target)}
                      className={`hover:underline focus:outline-none md:text-[18px] xl:text-[22px] transition-all duration-150 text-left
                        ${activeSection === item.target ? "font-bold" : ""}`}
                    >
                      {item.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <button
          aria-label="Apri menu"
          className="min-[1084px]:hidden text-white"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="block w-6 h-[2px] bg-white mb-1" />
          <span className="block w-6 h-[2px] bg-white mb-1" />
          <span className="block w-6 h-[2px] bg-white" />
        </button>
      </div>

      {/* Pannello mobile */}
      <div
        className={`min-[1084px]:hidden overflow-hidden transition-[max-height] duration-300
          ${open ? "max-h-96" : "max-h-0"} bg-[#004996]`}
      >
        <ul className="flex flex-col gap-4 p-5 text-white">
          {NAV.filter((item) => item.label).map((item) => (
            <li key={item.target}>
              {color ? (
                <a
                  href={`/${item.target}`}
                  onClick={(e) => onAnchorClick(e, item.target)}
                  className={`w-full text-left transition-all duration-150
                    ${activeSection === item.target ? "font-bold" : ""}`}
                >
                  {item.label}
                </a>
              ) : (
                <button
                  className={`w-full text-left transition-all duration-150
                    ${activeSection === item.target ? "font-bold" : ""}`}
                  onClick={() => scrollToTarget(item.target)}
                >
                  {item.label}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}
